// src/components/WorkOrderDetail.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { TeamPlanContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  updateDoc,
  writeBatch,
  serverTimestamp,
  orderBy,
  getDocs,
  addDoc,
  getDoc
} from 'firebase/firestore';
import { format } from 'date-fns'; 
import * as XLSX from 'xlsx'; 
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import WorkOrderPdf from './WorkOrderPdf';

// Import MUI
import {
  Box,
  Typography, CircularProgress, Button,
  IconButton, Paper,
  List, ListItem, ListItemIcon, ListItemText, Checkbox,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Tabs, 
  Tab,
  Alert,
  Autocomplete 
} from '@mui/material';

// Icone (invariate)
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; 
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges'; 
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'; 
import BuildIcon from '@mui/icons-material/Build'; 
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FileDownloadIcon from '@mui/icons-material/FileDownload'; 
import EditIcon from '@mui/icons-material/Edit'; 
import DeleteIcon from '@mui/icons-material/Delete'; 
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'; 
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; 


// --- Componente Task (MODIFICATO 'handleSaveDetails' e 'handleAddPart') ---
const WorkOrderItem = ({ item, workOrderId, userRole, canEdit, inventoryList, isLoadingInventory }) => {
  const { t } = useTranslation('workshop'); 
  const [notes, setNotes] = useState(item.notes || '');
  const [hours, setHours] = useState(item.hours || 0);
  const [parts, setParts] = useState(item.parts || []); 
  const [isSaving, setIsSaving] = useState(false);
  const [isPartsModalOpen, setIsPartsModalOpen] = useState(false);
  
  // --- 1. AGGIUNTO STATO DI SUCCESSO PER IL SALVATAGGIO ---
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setNotes(item.notes || '');
    setHours(item.hours || 0);
    setParts(item.parts || []);
  }, [item]);

  const itemRef = doc(db, "workOrders", workOrderId, "items", item.id);

  const handleToggleStatus = async () => {
    if (!canEdit) return;
    try {
      await updateDoc(itemRef, {
        status: item.status === 'Completed' ? 'Open' : 'Completed'
      });
    } catch (error) {
      console.error("Error updating item status:", error);
    }
  };

  // --- 2. MODIFICA: Aggiunto feedback visivo al salvataggio ---
  const handleSaveDetails = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setSaveSuccess(false); // Resetta
    try {
      await updateDoc(itemRef, {
        notes: notes,
        parts: parts, 
        hours: Number(hours) || 0
      });
      setSaveSuccess(true); // Imposta successo
      // Resetta il pulsante dopo 2 secondi
      setTimeout(() => setSaveSuccess(false), 2000); 
    } catch (error) {
      console.error("Error saving item details:", error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const getIcon = () => {
    switch (item.source) {
      case 'Issue': return <WarningAmberIcon color="error" />;
      case 'Lifing': return <PublishedWithChangesIcon color="warning" />;
      case 'Checklist': return <PlaylistAddCheckIcon color="primary" />;
      case 'Manual': return <AddCircleOutlineIcon color="info" />;
      default: return <BuildIcon />;
    }
  };

  const isComplete = item.status === 'Completed';

  const handleOpenPartsModal = () => setIsPartsModalOpen(true);
  const handleClosePartsModal = () => setIsPartsModalOpen(false);
  
  // --- 3. MODIFICA: handleAddPart ora accetta la quantità ---
  const handleAddPart = (selectedItem, quantity) => {
    if (!selectedItem || !quantity || quantity <= 0) return;
    setParts(prevParts => {
      const existingPart = prevParts.find(p => p.id === selectedItem.id);
      if (existingPart) {
        // Se esiste, somma la nuova quantità
        return prevParts.map(p => 
          p.id === selectedItem.id ? { ...p, quantity: p.quantity + quantity } : p
        );
      } else {
        // Se è nuovo, aggiungilo con la quantità specificata
        return [
          ...prevParts,
          {
            id: selectedItem.id,
            name: selectedItem.name,
            code: selectedItem.code || '',
            prezzoVendita: selectedItem.prezzoVendita || 0,
            quantity: quantity // Usa la quantità dal modale
          }
        ];
      }
    });
  };
  // --- FINE MODIFICA ---

  const handleRemovePart = (partId) => {
    setParts(prevParts => {
      const part = prevParts.find(p => p.id === partId);
      if (part.quantity > 1) {
        return prevParts.map(p =>
          p.id === partId ? { ...p, quantity: p.quantity - 1 } : p
        );
      } else {
        return prevParts.filter(p => p.id !== partId);
      }
    });
  };

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ mb: 1.5, border: '1px solid', borderColor: isComplete ? 'divider' : 'warning.main', backgroundColor: 'background.default' }}
      >
        <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, backgroundColor: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Checkbox
              edge="start"
              checked={isComplete}
              onChange={handleToggleStatus}
              onClick={(e) => e.stopPropagation()} 
              disabled={!canEdit}
              sx={{ mr: 1, p: 0.5 }}
            />
            <ListItemIcon sx={{ minWidth: 40, mr: 1 }}>{getIcon()}</ListItemIcon>
            <ListItemText
              primary={item.description}
              secondary={`${t('task.source_prefix')} ${item.source} ${item.sourceParent ? `(${item.sourceParent})` : ''}`}
              sx={{ textDecoration: isComplete ? 'line-through' : 'none', color: isComplete ? 'text.secondary' : 'text.primary' }}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('task.notes_label')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                disabled={!canEdit}
              />
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('task.parts_title')}</Typography>
                {parts.length === 0 && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {t('task.parts_empty')}
                  </Typography>
                )}
                <List dense disablePadding>
                  {parts.map(part => (
                    <ListItem 
                      key={part.id} 
                      disableGutters
                      secondaryAction={canEdit ? (
                        <IconButton size="small" onClick={() => handleRemovePart(part.id)}>
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    >
                      <ListItemText 
                        primary={`${part.quantity}x - ${part.name}`} 
                        secondary={`€${(part.prezzoVendita * part.quantity).toFixed(2)} (Ref: ${part.code || 'N/A'})`}
                      />
                    </ListItem>
                  ))}
                </List>
                {canEdit && (
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={handleOpenPartsModal}
                    sx={{ mt: 1 }}
                  >
                    {t('task.parts_add_button')}
                  </Button>
                )}
              </Box>

              <TextField
                label={t('task.labor_label')}
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                sx={{ maxWidth: '150px' }}
                disabled={!canEdit}
                inputProps={{ step: "0.25", min: "0" }}
              />
              
              {canEdit && (
                // --- 4. MODIFICA: Pulsante "Salva" con feedback ---
                <Button
                  onClick={handleSaveDetails}
                  disabled={isSaving || saveSuccess}
                  startIcon={isSaving ? <CircularProgress size={16} /> : (saveSuccess ? <CheckCircleIcon /> : <SaveIcon />)}
                  variant="outlined"
                  size="small"
                  color={saveSuccess ? 'success' : 'primary'}
                  sx={{ alignSelf: 'flex-end', minWidth: 100 }}
                >
                  {isSaving ? t('task.saving_button') : (saveSuccess ? t('task.saved_button') : t('task.save_button'))}
                </Button>
                // --- FINE MODIFICA ---
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* 5. MODIFICA: Passa 'onSelectItem' aggiornato (che ora è 'handleAddPart') */}
      <SelectItemModal
        open={isPartsModalOpen}
        onClose={handleClosePartsModal}
        inventoryList={inventoryList}
        isLoadingInventory={isLoadingInventory}
        onSelectItem={handleAddPart}
        t={t}
      />
    </>
  );
};
// --- Fine Componente Task ---


// --- Componente ImportTasksModal (Invariato) ---
const ImportTasksModal = ({ open, onClose, onImport, teamId, vehicleId, existingItems, type, t }) => {
  const [loading, setLoading] = useState(true);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open || !vehicleId || !teamId) return;
    setLoading(true);
    setSelectedIds([]);
    
    let q;
    if (type === 'Issue') {
      q = query( collection(db, "vehicleIssues"), where("teamId", "==", teamId), where("vehicleId", "==", vehicleId), where("status", "==", "Aperto") );
    } else if (type === 'Lifing') {
      q = query( collection(db, "vehicleComponents"), where("teamId", "==", teamId), where("vehicleId", "==", vehicleId), where("status", "!=", "In Workshop") );
    } else { // type === 'Checklist'
      q = query( collection(db, "checklistTemplates"), where("teamId", "==", teamId), where("type", "==", "Workshop"), orderBy("name") );
    }

    const unsub = onSnapshot(q, (snapshot) => {
      let items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (type === 'Lifing') {
        items = items.filter(item => (item.currentUsage >= item.totalLife));
      }
      const existingSources = existingItems.map(item => item.sourceId);
      items = items.filter(item => !existingSources.includes(item.id));
      setAvailableItems(items);
      setLoading(false);
    }, (error) => {
      console.error(`Error loading ${type}s:`, error);
      setLoading(false);
    });

    return () => unsub();
  }, [open, teamId, vehicleId, type, existingItems]);

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleImport = () => {
    const itemsToImport = availableItems.filter(item => selectedIds.includes(item.id));
    onImport(itemsToImport, type);
    onClose();
  };
  
  const getTitle = () => {
    if (type === 'Issue') return t('import_modal.title_issue');
    if (type === 'Lifing') return t('import_modal.title_lifing');
    if (type === 'Checklist') return t('import_modal.title_checklist');
    return t('import_modal.title_default');
  };
  
  const getLabel = (item) => {
    if (type === 'Issue') return item.description;
    if (type === 'Lifing') return t('import_modal.lifing_label', { name: item.name, currentUsage: item.currentUsage, totalLife: item.totalLife });
    if (type === 'Checklist') return item.name;
    return item.id;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress />}
        {!loading && availableItems.length === 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('import_modal.empty')}
          </Alert>
        )}
        {!loading && availableItems.length > 0 && (
          <List dense>
            {availableItems.map(item => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(item.id)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selectedIds.includes(item.id)} />
                  </ListItemIcon>
                  <ListItemText primary={getLabel(item)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('import_modal.cancel')}</Button>
        <Button onClick={handleImport} variant="contained" disabled={selectedIds.length === 0}>
          {t('import_modal.import_button', { count: selectedIds.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine ImportTasksModal ---


// --- 6. MODIFICA: Componente SelectItemModal (Aggiunto campo Quantità) ---
const SelectItemModal = ({ open, onClose, inventoryList, isLoadingInventory, onSelectItem, t }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  // Stato per la quantità
  const [quantity, setQuantity] = useState(1); 

  const handleSelect = () => {
    if (selectedItem && quantity > 0) {
      onSelectItem(selectedItem, quantity); // Passa anche la quantità
      setSelectedItem(null); // Resetta
      setQuantity(1); // Resetta
    }
  };

  const handleClose = () => {
    setSelectedItem(null);
    setQuantity(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('parts_modal.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete
            options={inventoryList}
            getOptionLabel={(option) => `${option.name} (Qty: ${option.quantity}) - €${option.prezzoVendita || 0}`}
            value={selectedItem}
            onChange={(event, newValue) => setSelectedItem(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingInventory}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('parts_modal.search_label')}
              />
            )}
          />
          {/* Campo Quantità Aggiunto */}
          <TextField
            label={t('parts_modal.quantity_label')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, step: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('import_modal.cancel')}</Button> 
        <Button onClick={handleSelect} variant="contained" disabled={!selectedItem || quantity <= 0}>
          {t('parts_modal.add_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine SelectItemModal ---


// --- Componente ManualTaskModal (Invariato) ---
const ManualTaskModal = ({ open, onClose, onSave, t }) => {
  const [description, setDescription] = useState('');

  const handleSave = () => {
    if (!description) return;
    onSave(description);
    setDescription(''); 
    onClose(); 
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('manual_modal.title')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="task-description"
          label={t('manual_modal.description_label')}
          type="text"
          fullWidth
          variant="standard"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('import_modal.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!description}>
          {t('manual_modal.add_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine ManualTaskModal ---


function WorkOrderDetail({ teamId, userRole, userId }) {
  const { t } = useTranslation('workshop');
  const { isTrial } = useContext(TeamPlanContext);
  const { workOrderId } = useParams();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [currentTab, setCurrentTab] = useState('Open'); 
  const [modalType, setModalType] = useState(null); 
  const [inventoryList, setInventoryList] = useState([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [isManualTaskModalOpen, setIsManualTaskModalOpen] = useState(false);

  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const canEdit = isManager && workOrder?.status === 'Open';
  
  const paperStyle = {
    p: { xs: 2, sm: 3 }, 
    mb: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper'
  };

  // Caricamenti (invariati)
  useEffect(() => {
    if (!workOrderId || !teamId) return;
    setIsLoadingOrder(true);
    const docRef = doc(db, "workOrders", workOrderId);
    const unsub = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().teamId === teamId) {
        setWorkOrder({ id: docSnap.id, ...docSnap.data() });
      } else {
        console.error("Work Order not found or access denied.");
        setWorkOrder(null);
        navigate('/workshop');
      }
      setIsLoadingOrder(false);
    }, (error) => {
      console.error("Error loading work order:", error);
      setIsLoadingOrder(false);
    });
    return () => unsub();
  }, [workOrderId, teamId, navigate]);

  useEffect(() => {
    if (!workOrderId) return;
    setIsLoadingItems(true);
    const itemsQuery = query(
      collection(db, "workOrders", workOrderId, "items"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(itemsQuery, (querySnapshot) => {
      setItems(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingItems(false);
    }, (error) => {
      console.error("Error loading work order items:", error);
      setIsLoadingItems(false);
    });
    return () => unsub();
  }, [workOrderId]);
  
  useEffect(() => {
    if (!teamId) return;
    setIsLoadingInventory(true);
    const q = query(
      collection(db, "inventory"), 
      where("teamId", "==", teamId), 
      orderBy("name", "asc")
    );
    const unsub = onSnapshot(q, (qs) => {
      setInventoryList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingInventory(false);
    }, (err) => { 
      console.error("Error loading inventory:", err); 
      setIsLoadingInventory(false); 
    });
    return () => unsub();
  }, [teamId]);
  
  
  // Funzione handleImportTasks (invariata)
  const handleImportTasks = async (itemsToImport, type) => {
    if (!workOrderId || !canEdit) return;
    const batch = writeBatch(db);
    itemsToImport.forEach(item => {
      const newItemRef = doc(collection(db, "workOrders", workOrderId, "items"));
      let task = {
        teamId: teamId,
        workOrderId: workOrderId,
        source: type, 
        sourceId: item.id, 
        status: "Open",
        createdAt: serverTimestamp(),
        notes: '',
        parts: [], 
        hours: 0,
      };
      if (type === 'Issue') {
        task.description = item.description;
      } else if (type === 'Lifing') {
        task.description = t('import_modal.lifing_replace', { name: item.name });
        task.notes = t('import_modal.lifing_note', { currentUsage: item.currentUsage, totalLife: item.totalLife, unit: item.unit || 'km' });
      } else if (type === 'Checklist') {
        (item.items || []).forEach((subItemText, index) => {
          const subItemRef = doc(collection(db, "workOrders", workOrderId, "items"));
          batch.set(subItemRef, {
            ...task,
            description: subItemText,
            source: 'Checklist',
            sourceId: `${item.id}_${index}`, 
            sourceParent: item.name 
          });
        });
        return; 
      }
      batch.set(newItemRef, task);
    });
    try {
      await batch.commit();
    } catch (error) {
      console.error("Error importing tasks:", error);
      alert(t('import_modal.import_error'));
    }
  };

  // Funzione handleSaveManualTask (invariata)
  const handleSaveManualTask = async (description) => {
    if (!workOrderId || !canEdit) return;
    try {
      await addDoc(collection(db, "workOrders", workOrderId, "items"), {
        teamId: teamId,
        workOrderId: workOrderId,
        source: 'Manual', 
        sourceId: null,
        description: description,
        status: "Open",
        createdAt: serverTimestamp(),
        notes: '',
        parts: [], 
        hours: 0,
      });
    } catch (error) {
      console.error("Error saving manual task:", error);
      alert(t('manual_modal.error_save')); 
    }
  };

  // Funzioni handleToggleWorkOrderStatus, handleDeleteWorkOrder, handleExportWorkOrder (invariate)
  const handleToggleWorkOrderStatus = async () => {
    if (!isManager) return; 
    const newStatus = workOrder.status === 'Open' ? 'Completed' : 'Open';
    const action = newStatus === 'Completed' ? t('detail.action_complete') : t('detail.action_reopen');
    if (window.confirm(t('detail.toggle_status_confirm', { action: action }))) {
      setIsLoadingOrder(true);
      try {
        const workOrderRef = doc(db, "workOrders", workOrderId);
        await updateDoc(workOrderRef, {
          status: newStatus,
          ...(newStatus === 'Completed' && { completedAt: serverTimestamp() }),
          ...(newStatus === 'Open' && { completedAt: null }) 
        });
      } catch (error) {
        console.error(`Error ${action} work order:`, error);
      } finally {
        setIsLoadingOrder(false); 
      }
    }
  };
  
  const handleDeleteWorkOrder = async () => {
    if (!isManager) {
      alert("Access denied.");
      return;
    }
    if (window.confirm(t('detail.delete_confirm', { title: workOrder.title }))) {
      try {
        const batch = writeBatch(db);
        const itemsQuery = query(collection(db, "workOrders", workOrderId, "items"));
        const itemsSnapshot = await getDocs(itemsQuery);
        itemsSnapshot.forEach(itemDoc => {
          batch.delete(itemDoc.ref);
        });
        const orderRef = doc(db, "workOrders", workOrderId);
        batch.delete(orderRef);
        await batch.commit();
        navigate('/workshop'); 
      } catch (error) {
        console.error("Error deleting work order:", error);
        alert(t('detail.delete_error'));
      }
    }
  };

  const handleExportWorkOrder = async () => {
    if (!workOrder || items.length === 0) {
      alert(t('detail.export_no_data'));
      return;
    }
    try {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      const teamData = teamSnap.data() || {};
      
      let proxyLogoUrl = null;
      if (teamData.logoUrlLight) {
          proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
      }

      const blob = await pdf(<WorkOrderPdf workOrder={workOrder} items={items} teamData={teamData} logoBase64={proxyLogoUrl} t={t} showWatermark={isTrial} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `WorkOrder_${workOrder.title.replace(/\s/g, '_')}_${workOrder.id.substring(0, 5)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF: " + error.message);
    }
  };
  
  // Filtro per le tab (invariato)
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const status = item.status === 'Completed' ? 'Completed' : 'Open';
      return status === currentTab;
    });
  }, [items, currentTab]);
  
  if (isLoadingOrder) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 10 }} />;
  }

  if (!workOrder) {
    return <Alert severity="error">{t('detail.not_found')}</Alert>;
  }
  
  const isWorkOrderClosed = workOrder.status === 'Completed';

  return (
    <>
      <Box className="no-print">
        {/* 1. Header (invariato) */}
        <Paper 
          elevation={0} 
          sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" onClick={() => navigate('/workshop')} sx={{ mr: 2, p: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                {workOrder.title}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {workOrder.vehicleName}
              </Typography>
            </Box>
            {isManager && (
              <Box sx={{ ml: 2 }}>
                <Tooltip title={t('detail.edit_tooltip')}>
                  <IconButton onClick={() => navigate(`/workshop/${workOrderId}/edit`)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('detail.delete_tooltip')}>
                  <IconButton onClick={handleDeleteWorkOrder}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={isWorkOrderClosed ? <LockIcon /> : <LockOpenIcon />}
              label={workOrder.status}
              color={isWorkOrderClosed ? 'default' : 'success'}
              sx={{ mr: 1 }}
            />
            <Tooltip title={t('detail.export_tooltip')}>
              <IconButton onClick={handleExportWorkOrder} size="large" disabled={items.length === 0}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            {isManager && (
              <Button
                variant="contained"
                color={isWorkOrderClosed ? "warning" : "success"}
                onClick={handleToggleWorkOrderStatus}
                disabled={isLoadingOrder}
                startIcon={isWorkOrderClosed ? <LockOpenIcon /> : <CheckCircleIcon />}
              >
                {isWorkOrderClosed ? t('detail.reopen_button') : t('detail.complete_button')}
              </Button>
            )}
          </Box>
        </Paper>
        
        {/* 2. Pannello Aggiungi Task (invariato) */}
        {canEdit && (
          <Paper elevation={0} sx={{ ...paperStyle, mb: 3 }}>
            <Typography variant="h5" gutterBottom>{t('detail.add_tasks_title')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsManualTaskModalOpen(true)}
                color="primary"
              >
                {t('detail.add_manual_task')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<WarningAmberIcon />}
                onClick={() => setModalType('Issue')}
              >
                {t('detail.import_issues')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PublishedWithChangesIcon />}
                onClick={() => setModalType('Lifing')}
              >
                {t('detail.import_lifing')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PlaylistAddCheckIcon />}
                onClick={() => setModalType('Checklist')}
              >
                {t('detail.import_checklists')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* 3. Lista Task (invariato) */}
        <Paper 
          elevation={0} 
          sx={{ border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
              <Tab label={t('detail.open_tasks')} value="Open" icon={<PendingIcon />} iconPosition="start" />
              <Tab label={t('detail.completed_tasks')} value="Completed" icon={<CheckCircleIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          
          <Box sx={{ p: { xs: 1, sm: 3 } }}>
            {isLoadingItems && <CircularProgress sx={{ display: 'block', m: 'auto', my: 4 }} />}
            {!isLoadingItems && filteredItems.length === 0 && (
              <Alert severity="success" sx={{ m: 2 }}>
                {t('detail.no_tasks', { tab: currentTab.toLowerCase() })}
              </Alert>
            )}
            
            <List sx={{ width: '100%' }}>
              {filteredItems.map(item => (
                <WorkOrderItem
                  key={item.id}
                  item={item}
                  workOrderId={workOrderId}
                  userRole={userRole}
                  canEdit={!isWorkOrderClosed} 
                  inventoryList={inventoryList} 
                  isLoadingInventory={isLoadingInventory}
                />
              ))}
            </List>
          </Box>
        </Paper>

        {/* 4. Modali per import (invariato) */}
        <ImportTasksModal
          open={modalType !== null}
          onClose={() => setModalType(null)}
          onImport={handleImportTasks}
          teamId={teamId}
          vehicleId={workOrder.vehicleId}
          existingItems={items} 
          type={modalType}
          t={t}
        />

        {/* 5. Modale Task Manuale (invariato) */}
        <ManualTaskModal
          open={isManualTaskModalOpen}
          onClose={() => setIsManualTaskModalOpen(false)}
          onSave={handleSaveManualTask}
          t={t}
        />
      </Box>
    </>
  );
}

export default WorkOrderDetail;